import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, ChevronRight, CheckCircle2,
  ArrowLeft, User, Trophy, ShoppingBag, BookOpen,
  Loader2, CreditCard, CalendarDays, Info, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const GOLF_GREEN = '#2E6A4C';
const CREAM = '#F5F2EB';

const GuestBooking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalSettings, setPortalSettings] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [step, setStep] = useState('browse');
  const [sessions, setSessions] = useState([]);
  const [stages, setStages] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [packs, setPacks] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [guestForm, setGuestForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('payment') === 'cancelled') toast.error('Paiement annulé');
    if (searchParams.get('payment') === 'success') setStep('success');
  }, [searchParams]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/public/guest/status`).then(r => r.json()).catch(() => ({ enabled: false })),
      fetch(`${API_URL}/api/client-portal/public-settings`).then(r => r.ok ? r.json() : null).catch(() => null)
    ]).then(([st, ps]) => {
      setStatus(st);
      setPortalSettings(ps);
      setLoading(false);
    });
  }, []);

  const loadData = async (section) => {
    setDataLoading(true);
    try {
      const urls = {
        cours: '/api/public/guest/sessions',
        stages: '/api/public/guest/stages',
        competitions: '/api/public/guest/competitions',
        boutique: '/api/public/guest/packs'
      };
      const res = await fetch(`${API_URL}${urls[section]}`);
      if (res.ok) {
        const data = await res.json();
        if (section === 'cours') setSessions(data);
        else if (section === 'stages') setStages(data);
        else if (section === 'competitions') setCompetitions(data);
        else setPacks(data);
      }
    } catch (e) {
      toast.error('Erreur de chargement');
    }
    setDataLoading(false);
  };

  useEffect(() => {
    if (status?.enabled && activeSection) loadData(activeSection);
  }, [activeSection, status]);

  const modules = status?.guest_modules || { cours: true, stages: true, competitions: true, boutique: true };

  const handleSelectItem = (item, type) => {
    if (item.is_full) { toast.error('Complet'); return; }
    setSelectedItem({ ...item, _type: type });
    setStep('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { first_name, last_name, email, phone } = guestForm;
    if (!first_name || !last_name || !email || !phone) { toast.error('Veuillez remplir tous les champs'); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { toast.error('Email invalide'); return; }
    setSubmitting(true);
    try {
      const urls = {
        cours: `${API_URL}/api/public/guest/courses/book`,
        stage: `${API_URL}/api/public/guest/stages/${selectedItem.id}/book`,
        competition: `${API_URL}/api/public/guest/competitions/${selectedItem.id}/book`,
        pack: `${API_URL}/api/public/guest/packs/purchase`
      };
      const body = selectedItem._type === 'cours' ? { ...guestForm, session_id: selectedItem.id }
        : selectedItem._type === 'pack' ? { ...guestForm, pack_id: selectedItem.id }
        : { ...guestForm };
      const res = await fetch(urls[selectedItem._type], {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      const result = await res.json();
      if (result.free) setStep('success');
      else if (result.checkout_url) window.location.href = result.checkout_url;
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const fmtDt = (dt) => { if (!dt) return ''; const d = typeof dt === 'string' ? parseISO(dt) : new Date(dt); return format(d, "EEEE d MMMM 'à' HH:mm", { locale: fr }); };
  const fmtDate = (dt) => { if (!dt) return ''; const d = typeof dt === 'string' ? parseISO(dt) : new Date(dt); return format(d, 'd MMMM yyyy', { locale: fr }); };
  const fmtDuration = (min) => { if (!min) return '1h'; const h = Math.floor(min / 60); const m = min % 60; if (h === 0) return `${m}min`; if (m === 0) return `${h}h`; return `${h}h${m}`; };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: CREAM }}><Loader2 className="animate-spin" size={32} style={{ color: GOLF_GREEN }} /></div>;
  if (!status?.enabled) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: CREAM }}>
      <Card className="max-w-md w-full text-center border-0 shadow-lg"><CardHeader>
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><Info className="w-8 h-8 text-amber-600" /></div>
        <CardTitle className="text-xl">Réservation indisponible</CardTitle>
        <CardDescription>La réservation en tant qu'invité n'est pas ouverte actuellement.</CardDescription>
      </CardHeader><CardContent><Button onClick={() => navigate('/')} variant="outline" className="mt-2"><ArrowLeft size={16} className="mr-2" /> Retour à l'accueil</Button></CardContent></Card>
    </div>
  );

  // Success page
  if (step === 'success') return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: CREAM }}>
      <Card className="max-w-md w-full text-center border-0 shadow-lg"><CardHeader>
        {portalSettings?.portal_logo?.data && <img src={`data:${portalSettings.portal_logo.content_type};base64,${portalSettings.portal_logo.data}`} alt="Logo" className="h-12 w-auto mx-auto mb-4 object-contain" />}
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${GOLF_GREEN}15` }}>
          <CheckCircle2 className="w-8 h-8" style={{ color: GOLF_GREEN }} />
        </div>
        <CardTitle className="text-xl" style={{ color: GOLF_GREEN }}>
          {selectedItem?._type === 'pack' ? 'Achat confirmé !' : 'Inscription confirmée !'}
        </CardTitle>
        <CardDescription className="text-base mt-2">
          {selectedItem ? (
            selectedItem._type === 'cours' ? `Votre réservation pour "${selectedItem.course_name}" est confirmée.` :
            selectedItem._type === 'stage' ? `Votre inscription au stage "${selectedItem.name}" est confirmée.` :
            selectedItem._type === 'competition' ? `Votre inscription à "${selectedItem.name}" est confirmée.` :
            `Votre pack "${selectedItem.name}" a été acheté avec succès.`
          ) : 'Votre réservation est confirmée.'}
        </CardDescription>
      </CardHeader><CardContent className="space-y-4">
        <div className="rounded-xl p-4 text-sm" style={{ backgroundColor: `${GOLF_GREEN}10`, color: GOLF_GREEN }}>
          <p className="font-medium mb-1">Un email de confirmation vous a été envoyé</p>
          <p className="text-xs opacity-75">Vérifiez vos spams si vous ne le trouvez pas</p>
        </div>
        <div className="rounded-xl p-4 text-left border bg-white">
          <p className="font-semibold mb-3 text-sm flex items-center gap-2" style={{ color: GOLF_GREEN }}><UserPlus size={16} />Créez votre compte gratuitement</p>
          <p className="text-xs text-neutral-600 mb-3">En créant un compte, vous pourrez retrouver vos réservations, gérer votre profil et profiter de tarifs membres exclusifs.</p>
          <Button onClick={() => navigate('/?signup=true')} className="w-full text-white font-medium" style={{ backgroundColor: GOLF_GREEN }} data-testid="guest-create-account-btn">
            <UserPlus size={16} className="mr-2" />Créer mon compte
          </Button>
        </div>
        <button onClick={() => { setStep('browse'); setActiveSection(null); setSelectedItem(null); }} className="text-sm underline" style={{ color: GOLF_GREEN }}>Faire une autre réservation</button>
      </CardContent></Card>
    </div>
  );

  // Guest form
  if (step === 'form' && selectedItem) return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: CREAM }}>
      <div className="max-w-lg mx-auto">
        <button onClick={() => setStep('browse')} className="flex items-center gap-1.5 text-sm mb-4 hover:opacity-75" style={{ color: GOLF_GREEN }} data-testid="guest-form-back-btn">
          <ArrowLeft size={16} /> Retour
        </button>
        {portalSettings?.portal_logo?.data && <div className="text-center mb-4"><img src={`data:${portalSettings.portal_logo.content_type};base64,${portalSettings.portal_logo.data}`} alt="Logo" className="h-10 w-auto mx-auto object-contain" /></div>}
        <Card className="mb-4 border-0 shadow-sm overflow-hidden">
          <div className="h-1.5" style={{ backgroundColor: GOLF_GREEN }} />
          <CardContent className="p-4">
            <h3 className="font-bold text-base mb-1">{selectedItem._type === 'cours' ? selectedItem.course_name : selectedItem.name}</h3>
            {selectedItem._type === 'cours' && <div className="text-sm text-neutral-600 space-y-0.5">
              <p className="flex items-center gap-1.5"><Calendar size={13} /> {fmtDt(selectedItem.datetime)}</p>
              {selectedItem.coach_name && <p className="flex items-center gap-1.5"><User size={13} /> {selectedItem.coach_name}</p>}
            </div>}
            {selectedItem._type === 'stage' && <div className="text-sm text-neutral-600 space-y-0.5">
              <p className="flex items-center gap-1.5"><Calendar size={13} /> Du {fmtDate(selectedItem.start_date)} au {fmtDate(selectedItem.end_date)}</p>
              {selectedItem.coach_name && <p className="flex items-center gap-1.5"><User size={13} /> {selectedItem.coach_name}</p>}
            </div>}
            {selectedItem._type === 'competition' && <div className="text-sm text-neutral-600 space-y-0.5">
              <p className="flex items-center gap-1.5"><Calendar size={13} /> {fmtDate(selectedItem.start_date)}</p>
              {selectedItem.format && <p className="flex items-center gap-1.5"><Trophy size={13} /> {selectedItem.format}</p>}
            </div>}
            {selectedItem._type === 'pack' && <p className="text-sm text-neutral-600">{selectedItem.hours}h de cours individuels</p>}
            {selectedItem.price > 0 ? (
              <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-sm font-bold" style={{ backgroundColor: GOLF_GREEN }}>{selectedItem.price}€</div>
            ) : selectedItem._type !== 'pack' ? (
              <Badge className="mt-2 bg-green-100 text-green-700">Gratuit</Badge>
            ) : null}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base" style={{ color: GOLF_GREEN }}>Vos coordonnées</CardTitle>
            <CardDescription className="text-xs">Ces informations sont nécessaires pour votre réservation</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="guest-booking-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Prénom *</Label>
                  <Input value={guestForm.first_name} onChange={(e) => setGuestForm({ ...guestForm, first_name: e.target.value })} placeholder="Jean" className="h-10" required data-testid="guest-first-name" /></div>
                <div className="space-y-1"><Label className="text-xs">Nom *</Label>
                  <Input value={guestForm.last_name} onChange={(e) => setGuestForm({ ...guestForm, last_name: e.target.value })} placeholder="Dupont" className="h-10" required data-testid="guest-last-name" /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Email *</Label>
                <Input type="email" value={guestForm.email} onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })} placeholder="jean@email.com" className="h-10" required data-testid="guest-email" /></div>
              <div className="space-y-1"><Label className="text-xs">Téléphone *</Label>
                <Input type="tel" value={guestForm.phone} onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })} placeholder="06 12 34 56 78" className="h-10" required data-testid="guest-phone" /></div>
              <Button type="submit" className="w-full h-11 text-white font-medium text-base" style={{ backgroundColor: GOLF_GREEN }} disabled={submitting} data-testid="guest-submit-btn">
                {submitting ? <Loader2 className="animate-spin mr-2" size={18} /> : <CreditCard size={18} className="mr-2" />}
                {selectedItem.price > 0 ? `Payer ${selectedItem.price}€ et confirmer` : 'Confirmer mon inscription'}
              </Button>
              <p className="text-[10px] text-center text-neutral-400">{selectedItem.price > 0 ? 'Vous serez redirigé vers notre plateforme de paiement sécurisée' : 'Vous recevrez un email de confirmation'}</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Main browse view
  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM, fontFamily: "'Manrope', sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-20 border-b bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeSection && <button onClick={() => { setActiveSection(null); }} data-testid="guest-nav-back"><ArrowLeft size={20} style={{ color: GOLF_GREEN }} /></button>}
            {portalSettings?.portal_logo?.data ? (
              <img src={`data:${portalSettings.portal_logo.content_type};base64,${portalSettings.portal_logo.data}`} alt="Logo" className="h-8 w-auto object-contain" />
            ) : <span className="font-bold text-lg" style={{ color: GOLF_GREEN }}>{status?.golf_name || 'Golf'}</span>}
          </div>
          <Badge variant="outline" className="text-xs font-medium border-amber-300 text-amber-700 bg-amber-50">Mode Invité</Badge>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Home */}
        {!activeSection && (
          <div className="space-y-4">
            <div className="text-center py-6">
              <h1 className="text-2xl font-bold mb-2" style={{ color: GOLF_GREEN }}>Bienvenue au {status?.golf_name || 'Golf'}</h1>
              <p className="text-neutral-500 text-sm">Réservez vos activités sans créer de compte</p>
            </div>
            <div className={`grid ${Object.values(modules).filter(Boolean).length <= 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-3`} data-testid="guest-home-menu">
              {[
                { id: 'cours', icon: CalendarDays, label: 'Cours', desc: 'Individuels & Collectifs', color: GOLF_GREEN },
                { id: 'stages', icon: BookOpen, label: 'Stages', desc: 'Stages & Clinics', color: '#1B5E8A' },
                { id: 'competitions', icon: Trophy, label: 'Compétitions', desc: 'Tournois & Challenges', color: '#8B5E3C' },
                { id: 'boutique', icon: ShoppingBag, label: 'Boutique', desc: 'Packs de cours', color: '#6B4C8A' },
              ].filter(s => modules[s.id]).map(s => (
                <button key={s.id} onClick={() => setActiveSection(s.id)} className="text-left rounded-2xl p-5 border-0 shadow-sm hover:shadow-md transition-all bg-white group" data-testid={`guest-menu-${s.id}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110" style={{ backgroundColor: `${s.color}12` }}>
                    <s.icon size={22} style={{ color: s.color }} />
                  </div>
                  <h3 className="font-bold text-sm mb-0.5" style={{ color: s.color }}>{s.label}</h3>
                  <p className="text-xs text-neutral-400">{s.desc}</p>
                </button>
              ))}
            </div>
            <div className="text-center pt-4 pb-2">
              <p className="text-xs text-neutral-400 mb-2">Vous avez déjà un compte ?</p>
              <Button variant="outline" onClick={() => navigate('/')} className="text-sm font-medium" style={{ borderColor: GOLF_GREEN, color: GOLF_GREEN }} data-testid="guest-go-login-btn">Se connecter</Button>
            </div>
          </div>
        )}

        {/* Cours - Flat list like portal */}
        {activeSection === 'cours' && (
          <div className="space-y-2.5">
            <h2 className="text-lg font-bold mb-3" style={{ color: GOLF_GREEN }}>Cours disponibles</h2>
            {dataLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={28} style={{ color: GOLF_GREEN }} /></div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12">
                <CalendarDays size={40} className="mx-auto text-neutral-300 mb-3" />
                <p className="text-neutral-500 text-sm">Aucun cours disponible</p>
              </div>
            ) : sessions.map((s, i) => {
              const empColor = s.employee_color || '#1B8066';
              const durationLabel = fmtDuration(s.duration_minutes);
              const isFull = s.is_full;
              const priceLabel = s.price > 0 ? `${s.price}€` : 'Gratuit';
              const priceStyle = s.price > 0 
                ? { backgroundColor: GOLF_GREEN, color: 'white' }
                : { backgroundColor: '#dcfce7', color: '#166534' };
              return (
                <div
                  key={s.id || i}
                  className={`rounded-lg overflow-hidden ${isFull ? 'opacity-50' : ''}`}
                  style={{ backgroundColor: empColor + '15', borderLeft: `4px solid ${empColor}` }}
                  data-testid={`guest-course-${s.id || i}`}
                >
                  <div className="px-3 py-2.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-neutral-900">
                        {s.course_name}{s.coach_name ? <span className="font-normal text-neutral-500"> avec </span> : ''}{s.coach_name && <span className="text-neutral-900 font-bold">{s.coach_name}</span>}
                      </p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {s.datetime ? format(parseISO(s.datetime), "EEEE d MMMM 'à' HH:mm", { locale: fr }) : ''}
                        <span className="text-neutral-300 mx-1.5">·</span>
                        <span className="text-neutral-400">{durationLabel}</span>
                      </p>
                    </div>
                    {isFull ? (
                      <span className="text-[11px] bg-red-100 text-red-600 font-semibold px-3 py-1.5 rounded-lg flex-shrink-0">Complet</span>
                    ) : (
                      <button
                        onClick={() => handleSelectItem(s, 'cours')}
                        className="text-[11px] text-white font-semibold px-3 py-1.5 rounded-lg hover:opacity-90 active:scale-95 transition-all flex-shrink-0"
                        style={{ backgroundColor: GOLF_GREEN }}
                        data-testid={`guest-course-book-${s.id || i}`}
                      >
                        {priceLabel === 'Gratuit' ? 'Gratuit' : priceLabel}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stages */}
        {activeSection === 'stages' && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold" style={{ color: GOLF_GREEN }}>Stages disponibles</h2>
            {dataLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={28} style={{ color: GOLF_GREEN }} /></div>
            ) : stages.length === 0 ? (
              <div className="text-center py-8"><BookOpen size={40} className="mx-auto text-neutral-300 mb-3" /><p className="text-neutral-500 text-sm">Aucun stage disponible</p></div>
            ) : stages.map(s => (
              <Card key={s.id} className="border-0 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer" onClick={() => handleSelectItem(s, 'stage')} data-testid={`guest-stage-${s.id}`}>
                <CardContent className="p-0"><div className="flex">
                  <div className="w-1.5" style={{ backgroundColor: s.is_full ? '#ef4444' : '#1B5E8A' }} />
                  <div className="flex-1 p-4"><div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-sm">{s.name}</h4>
                      <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><Calendar size={11} /> {fmtDate(s.start_date)} - {fmtDate(s.end_date)}</p>
                      {s.coach_name && <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><User size={11} /> {s.coach_name}</p>}
                      {s.description && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{s.description}</p>}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      {s.price > 0 ? <span className="font-bold text-sm" style={{ color: '#1B5E8A' }}>{s.price}€</span> : <Badge className="bg-green-100 text-green-700 text-[10px]">Gratuit</Badge>}
                      {s.is_full && <Badge className="bg-red-100 text-red-600 text-[10px] mt-1 block">Complet</Badge>}
                    </div>
                  </div></div>
                  <div className="flex items-center pr-3"><ChevronRight size={16} className="text-neutral-300" /></div>
                </div></CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Competitions */}
        {activeSection === 'competitions' && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold" style={{ color: GOLF_GREEN }}>Compétitions</h2>
            {dataLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={28} style={{ color: GOLF_GREEN }} /></div>
            ) : competitions.length === 0 ? (
              <div className="text-center py-8"><Trophy size={40} className="mx-auto text-neutral-300 mb-3" /><p className="text-neutral-500 text-sm">Aucune compétition disponible</p></div>
            ) : competitions.map(c => (
              <Card key={c.id} className="border-0 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer" onClick={() => handleSelectItem(c, 'competition')} data-testid={`guest-comp-${c.id}`}>
                <CardContent className="p-0"><div className="flex">
                  <div className="w-1.5" style={{ backgroundColor: c.is_full ? '#ef4444' : '#8B5E3C' }} />
                  <div className="flex-1 p-4"><div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-sm">{c.name}</h4>
                      <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1"><Calendar size={11} /> {fmtDate(c.start_date)}</p>
                      {c.format && <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><Trophy size={11} /> {c.format}</p>}
                      {c.description && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{c.description}</p>}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      {c.price > 0 ? <span className="font-bold text-sm" style={{ color: '#8B5E3C' }}>{c.price}€</span> : <Badge className="bg-green-100 text-green-700 text-[10px]">Gratuit</Badge>}
                      {c.is_full && <Badge className="bg-red-100 text-red-600 text-[10px] mt-1 block">Complet</Badge>}
                    </div>
                  </div></div>
                  <div className="flex items-center pr-3"><ChevronRight size={16} className="text-neutral-300" /></div>
                </div></CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Boutique */}
        {activeSection === 'boutique' && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold" style={{ color: GOLF_GREEN }}>Packs de cours</h2>
            <p className="text-xs text-neutral-500">Achetez un carnet de cours individuels</p>
            {dataLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={28} style={{ color: GOLF_GREEN }} /></div>
            ) : packs.length === 0 ? (
              <div className="text-center py-8"><ShoppingBag size={40} className="mx-auto text-neutral-300 mb-3" /><p className="text-neutral-500 text-sm">Aucun pack disponible</p></div>
            ) : packs.map(p => (
              <Card key={p.id} className="border-0 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer" onClick={() => handleSelectItem(p, 'pack')} data-testid={`guest-pack-${p.id}`}>
                <CardContent className="p-0"><div className="flex">
                  <div className="flex-1 p-4">
                    <h4 className="font-bold text-sm">{p.name}</h4>
                    <p className="text-xs mt-1 font-semibold" style={{ color: '#6B4C8A' }}>{p.hours} heures</p>
                    {p.description && <p className="text-xs text-neutral-400 mt-1">{p.description}</p>}
                  </div>
                  <div className="flex flex-col items-center justify-center px-4 min-w-[90px]" style={{ backgroundColor: `${GOLF_GREEN}08` }}>
                    <span className="font-bold text-lg" style={{ color: GOLF_GREEN }}>{p.price}€</span>
                    <ChevronRight size={14} className="text-neutral-300 mt-1" />
                  </div>
                </div></CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestBooking;
